import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './UserProfile.css';
import DocumentsUpload from './partials/DocumentsUpload';
import PrivateDocuments from './partials/PrivateDocuments';



import api, { userService, projectService } from '../services/api';

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState([]);
  const [projects, setProjects] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [avatarFile, setAvatarFile] = useState(null);
  useEffect(() => {
    // Load project list for admin project editing
    (async () => {
      try {
        const data = await projectService.getAll();
        setProjects(data);
      } catch (e) {
        // non-fatal
      }
    })();
  }, []);

  const [avatarPreview, setAvatarPreview] = useState(null);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}'); } catch(_) { return {}; } })();
  const canEditRates = currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager');
  const canEditImportNumber = currentUser && currentUser.role === 'admin';
  const canViewBillingRateHistory = currentUser && currentUser.role === 'admin';

  const billingRateHistory = useMemo(() => (
    Array.isArray(user?.billing_rate_history) ? user.billing_rate_history : []
  ), [user?.billing_rate_history]);
  const formatMoney = (value) => `${(parseFloat(value) || 0).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const [annualRatePrompt, setAnnualRatePrompt] = useState({ visible: false, formVisible: false, anniversaryDate: '' });
  const [rateAdjustmentForm, setRateAdjustmentForm] = useState({ effective_from: '', weekday_rate: '', weekend_night_rate: '' });
  const [internalNumberConflict, setInternalNumberConflict] = useState(null);
  const [internalNumberConflictSaving, setInternalNumberConflictSaving] = useState(false);

  const formatDate = (value) => value ? new Date(value).toLocaleDateString('sk-SK') : '—';


  const [changePassword, setChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Cleanup avatar preview URL on unmount
  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const fetchUserData = useCallback(async () => {
    try {
      const data = await userService.getById(id);
      setUser(data);
      setFormData(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [id]);


  useEffect(() => {
    if (!canViewBillingRateHistory || !user?.collaboration_start_date || user.blocked) return;

    const startDate = new Date(user.collaboration_start_date);
    if (Number.isNaN(startDate.getTime())) return;

    const anniversaryDate = new Date(startDate);
    anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    anniversaryDate.setHours(0, 0, 0, 0);
    if (anniversaryDate > today) return;

    const anniversaryDateString = anniversaryDate.toISOString().slice(0, 10);
    const alreadyAdjusted = billingRateHistory.some(rate => String(rate.effective_from).slice(0, 10) >= anniversaryDateString && rate.source !== 'default');
    if (alreadyAdjusted) return;

    const promptKey = `annual-rate-prompt-${user.id}-${anniversaryDateString}`;
    if (sessionStorage.getItem(promptKey)) return;
    sessionStorage.setItem(promptKey, '1');

    const latestRate = [...billingRateHistory].reverse().find(rate => rate.weekday_rate !== undefined) || {};
    setRateAdjustmentForm({
      effective_from: today.toISOString().slice(0, 10),
      weekday_rate: latestRate.weekday_rate ?? 40,
      weekend_night_rate: latestRate.weekend_night_rate ?? 45
    });
    setAnnualRatePrompt({ visible: true, formVisible: false, anniversaryDate: anniversaryDateString });
  }, [canViewBillingRateHistory, user, billingRateHistory]);

  const fetchUserTerms = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${id}/terms-history`);
      setAcceptedTerms(data);
    } catch (err) {
      console.error('Error fetching terms:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchUserData();
    fetchUserTerms();
  }, [fetchUserData, fetchUserTerms]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resizeImage = (file, maxSize = 512, quality = 0.85) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      const scale = Math.min(maxSize / width, maxSize / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (!blob) return reject(new Error('Image processing failed'));
        resolve(blob);
      }, 'image/jpeg', quality);
    };
    img.onerror = (e) => reject(e);
    img.src = url;
  });

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Súbor je príliš veľký. Maximálna veľkosť je 5MB.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Prosím vyberte obrázok.');
        return;
      }
      try {
        const resized = await resizeImage(file, 512, 0.85);
        setAvatarFile(resized);
        setAvatarPreview(URL.createObjectURL(resized));
      } catch (err) {
        console.error('Image resize failed', err);
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
      }
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const buildProfilePayload = (extra = {}) => {
    const payload = { ...formData, ...extra };
    if (!canEditRates) {
      delete payload.hourly_rate_weekday;
      delete payload.hourly_rate_weekend;
      delete payload.hourly_rate_holiday;
    }
    if (!canEditImportNumber) {
      delete payload.internal_number;
    }
    return payload;
  };

  const saveProfile = async (extra = {}) => {
    const updated = await userService.update(id, buildProfilePayload(extra));
    if (avatarFile) {
      const fd = new FormData();
      fd.append('avatar', avatarFile, 'avatar.jpg');
      await api.post(`/users/${id}/avatar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return updated;
  };

  const finishProfileSave = async (updated) => {
    setUser(updated);
    setFormData(updated);
    setEditMode(false);
    setAvatarFile(null);
    setAvatarPreview(null);
    setInternalNumberConflict(null);
    alert('Používateľ bol úspešne aktualizovaný');
    await fetchUserData();
  };

  const getConflictEffectiveFrom = () => (
    formData.collaboration_start_date || user?.collaboration_start_date || new Date().toISOString().slice(0, 10)
  );

  const showInternalNumberConflict = (err) => {
    const conflict = err.response?.data;
    if (err.response?.status !== 409 || conflict?.error !== 'INTERNAL_NUMBER_CONFLICT') return false;
    setInternalNumberConflict({
      ...conflict,
      effective_from: getConflictEffectiveFrom()
    });
    return true;
  };

  const resolveInternalNumberConflict = async (action) => {
    if (!internalNumberConflict || internalNumberConflictSaving) return;

    const extra = action === 'block_existing'
      ? {
          internal_number_conflict_action: 'block_existing',
          internal_number_conflict_user_id: internalNumberConflict.conflict_user?.id
        }
      : {
          internal_number: internalNumberConflict.suggested_internal_number,
          internal_number_conflict_action: 'use_next_available'
        };

    setInternalNumberConflictSaving(true);
    try {
      const updated = await saveProfile(extra);
      await finishProfileSave(updated);
    } catch (retryError) {
      alert(`Chyba pri riešení konfliktu interného čísla: ${retryError.response?.data?.message || retryError.response?.data?.error || retryError.message}`);
    } finally {
      setInternalNumberConflictSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const updated = await saveProfile();
      await finishProfileSave(updated);
    } catch (err) {
      if (showInternalNumberConflict(err)) return;
      alert(`Chyba: ${err.response?.data?.message || err.response?.data?.error || err.message}`);
    }
  };

  const handleRateAdjustmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await userService.addBillingRate(id, {
        effective_from: rateAdjustmentForm.effective_from,
        weekday_rate: rateAdjustmentForm.weekday_rate,
        weekend_night_rate: rateAdjustmentForm.weekend_night_rate
      });
      setUser(prev => ({ ...prev, billing_rate_history: result.billing_rate_history || prev.billing_rate_history || [] }));
      setAnnualRatePrompt({ visible: false, formVisible: false, anniversaryDate: '' });
      alert('Fakturačné sadzby boli uložené.');
      await fetchUserData();
    } catch (err) {
      alert(`Chyba pri ukladaní fakturačných sadzieb: ${err.response?.data?.message || err.response?.data?.error || err.message}`);
    }
  };


  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Nové heslá sa nezhodujú');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      alert('Heslo musí mať aspoň 6 znakov');
      return;
    }
    
    try {
      await api.post(`/users/${id}/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      alert('Heslo bolo úspešne zmenené');
      setChangePassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      alert(`Chyba: ${err.message}`);
    }
  };

  const downloadPDF = async (acceptanceId) => {
    try {
      const { data: text } = await api.get(`/terms/acceptance/${acceptanceId}/pdf`, { responseType: 'text' });
      const blob = new Blob([text], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `terms_acceptance_${acceptanceId}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Chyba pri sťahovaní certifikátu: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="user-profile-loading">⏳ Načítavam...</div>;
  }

  if (error) {
    return <div className="user-profile-error">❌ {error}</div>;
  }

  if (!user) {
    return <div className="user-profile-error">❌ Používateľ nebol nájdený</div>;
  }

  return (
    <div className="user-profile-container">
      {annualRatePrompt.visible && (
        <div className="user-profile-modal-backdrop">
          <div className="user-profile-modal">
            {!annualRatePrompt.formVisible ? (
              <>
                <h2>Upraviť fakturačné ceny?</h2>
                <p>Používateľ pracuje viac ako rok od {formatDate(user.collaboration_start_date)}.</p>
                <p>Chceš upraviť ceny pre tohto používateľa?</p>
                <div className="user-profile-modal-actions">
                  <button
                    type="button"
                    className="user-profile-save-button"
                    onClick={() => setAnnualRatePrompt(prev => ({ ...prev, formVisible: true }))}
                  >
                    Áno, upraviť ceny
                  </button>
                  <button
                    type="button"
                    className="user-profile-cancel-button"
                    onClick={() => setAnnualRatePrompt({ visible: false, formVisible: false, anniversaryDate: '' })}
                  >
                    Nie teraz
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleRateAdjustmentSubmit} className="user-profile-rate-form">
                <h2>Nové fakturačné sadzby</h2>
                <div className="user-profile-form-group">
                  <label>Platné od dátumu:</label>
                  <input
                    type="date"
                    value={rateAdjustmentForm.effective_from}
                    onChange={(e) => setRateAdjustmentForm(prev => ({ ...prev, effective_from: e.target.value }))}
                    required
                  />
                </div>
                <div className="user-profile-form-group">
                  <label>Nová suma týždeň / pracovný deň:</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={rateAdjustmentForm.weekday_rate}
                    onChange={(e) => setRateAdjustmentForm(prev => ({ ...prev, weekday_rate: e.target.value }))}
                    required
                  />
                </div>
                <div className="user-profile-form-group">
                  <label>Nová suma víkend / nočná:</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={rateAdjustmentForm.weekend_night_rate}
                    onChange={(e) => setRateAdjustmentForm(prev => ({ ...prev, weekend_night_rate: e.target.value }))}
                    required
                  />
                </div>
                <div className="user-profile-modal-actions">
                  <button type="submit" className="user-profile-save-button">Uložiť sadzby</button>
                  <button
                    type="button"
                    className="user-profile-cancel-button"
                    onClick={() => setAnnualRatePrompt({ visible: false, formVisible: false, anniversaryDate: '' })}
                  >
                    Zrušiť
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {internalNumberConflict && (
        <div className="user-profile-modal-backdrop">
          <div className="user-profile-modal user-profile-conflict-modal">
            <h2>Konflikt interného čísla</h2>
            <p className="user-profile-conflict-summary">
              Interné číslo <strong>{internalNumberConflict.internal_number}</strong> už používa používateľ{' '}
              <strong>{internalNumberConflict.conflict_user?.full_name || internalNumberConflict.conflict_user?.username || 'bez mena'}</strong>.
            </p>
            <p>
              Pre používateľa <strong>{formData.full_name || user.full_name}</strong> je začiatok spolupráce{' '}
              <strong>{formatDate(internalNumberConflict.effective_from)}</strong>.
            </p>
            <div className="user-profile-conflict-options">
              <button
                type="button"
                className="user-profile-save-button"
                disabled={internalNumberConflictSaving}
                onClick={() => resolveInternalNumberConflict('block_existing')}
              >
                Zablokovať pôvodného používateľa a priradiť číslo {internalNumberConflict.internal_number}
              </button>
              {internalNumberConflict.suggested_internal_number && (
                <button
                  type="button"
                  className="user-profile-secondary-button"
                  disabled={internalNumberConflictSaving}
                  onClick={() => resolveInternalNumberConflict('use_next_available')}
                >
                  Použiť ďalšie voľné číslo {internalNumberConflict.suggested_internal_number}
                </button>
              )}
            </div>
            <p className="user-profile-conflict-note">
              Pri blokovaní sa pôvodnému používateľovi ukončí história čísla ku dňu pred začiatkom spolupráce nového používateľa.
            </p>
            <div className="user-profile-modal-actions">
              <button
                type="button"
                className="user-profile-cancel-button"
                disabled={internalNumberConflictSaving}
                onClick={() => setInternalNumberConflict(null)}
              >
                Zrušiť bez uloženia
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="user-profile-header">
        <button onClick={() => navigate('/users')} className="user-profile-back-button">
          ← Späť
        </button>
        <h1>👤 Profil používateľa</h1>
      </div>

      <div className="user-profile-content">
        {/* Avatar Section */}
        <div className="user-profile-avatar-section">
          <div className="user-profile-avatar-container">
            {avatarPreview ? (
              <img 
                src={avatarPreview} 
                alt="Avatar Preview" 
                className="user-profile-avatar"
              />
            ) : user.avatar && user.avatar !== 'null' && user.avatar !== '' ? (
              <img 
                src={`/api/users/${id}/avatar`} 
                alt="Avatar" 
                className="user-profile-avatar"
              />
            ) : (
              <div className="user-profile-avatar-placeholder">
                {user.full_name ? user.full_name.charAt(0).toUpperCase() : user.username ? user.username.charAt(0).toUpperCase() : '?'}
              </div>
            )}
          </div>
          {editMode && (
            <div className="user-profile-avatar-upload">
              <label htmlFor="avatar-upload" className="user-profile-avatar-upload-label">
                📷 Zmeniť avatar
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>

        {/* User Information Section */}
        <div className="user-profile-section">
          <div className="user-profile-section-header">
            <h2>📋 Informácie o používateľovi</h2>
            {!editMode && (
              <button onClick={() => setEditMode(true)} className="user-profile-edit-button">
                ✏️ Upraviť
              </button>
            )}
          </div>

          {editMode ? (
            <form onSubmit={handleSubmit} className="user-profile-edit-form">
              <div className="user-profile-form-group">
                <label>Používateľské meno:</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username || ''}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="user-profile-form-group">
                <label>Celé meno:</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name || ''}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {canEditImportNumber && (
                <div className="user-profile-form-group">
                  <label>Interné číslo pre import:</label>
                  <input
                    type="text"
                    name="internal_number"
                    value={formData.internal_number || ''}
                    onChange={handleInputChange}
                    placeholder="napr. 254"
                  />
                </div>
              )}

              {canEditImportNumber && (
                <div className="user-profile-form-group">
                  <label>Začiatok spolupráce:</label>
                  <input
                    type="date"
                    name="collaboration_start_date"
                    value={formData.collaboration_start_date || ''}
                    onChange={handleInputChange}
                  />
                </div>
              )}


              <div className="user-profile-form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="user-profile-form-group">
                <label>Telefón:</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="user-profile-form-group">
                <label>Rola:</label>
                <select
                  name="role"
                  value={formData.role || 'user'}
                  onChange={handleInputChange}
                  required
                >
                  <option value="user">User</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {user && user.role === 'admin' && (
                <div className="user-profile-form-group">
                  <label>Projekt:</label>
                  <select
                    name="project_id"
                    value={formData.project_id || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Žiadny projekt</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="user-profile-form-group">
                <label>Hodinová sadzba (pracovný deň) €:</label>
                {canEditRates ? (
                  <input
                    type="number"
                    step="0.01"
                    name="hourly_rate_weekday"
                    value={formData.hourly_rate_weekday || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  <div>{formData.hourly_rate_weekday || 0} €</div>
                )}
              </div>

              <div className="user-profile-form-group">
                <label>Hodinová sadzba (víkend) €:</label>
                {canEditRates ? (
                  <input
                    type="number"
                    step="0.01"
                    name="hourly_rate_weekend"
                    value={formData.hourly_rate_weekend || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  <div>{formData.hourly_rate_weekend || 0} €</div>
                )}
              </div>

              <div className="user-profile-form-group">
                <label>Hodinová sadzba (sviatok) €:</label>
                {canEditRates ? (
                  <input
                    type="number"
                    step="0.01"
                    name="hourly_rate_holiday"
                    value={formData.hourly_rate_holiday || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  <div>{formData.hourly_rate_holiday || 0} €</div>
                )}
              </div>

              <div className="user-profile-form-group">
                <label>Ubytovanie:</label>
                <select
                  name="accommodation"
                  value={formData.accommodation || ''}
                  onChange={handleInputChange}
                >
                  <option value="">-- Vybrať --</option>
                  <option value="self">Platí sám</option>
                  <option value="company">Platí firma</option>
                  <option value="self_6">Platí sám (6 €)</option>
                  <option value="company_6">Platí firma (6 €)</option>
                </select>
              </div>

              <div className="user-profile-form-actions">
                <button type="submit" className="user-profile-save-button">
                  💾 Uložiť
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setEditMode(false);
                    setFormData(user);
                  }}
                  className="user-profile-cancel-button"
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          ) : (
            <div className="user-profile-info">
              <div className="user-profile-info-row">
                <span className="user-profile-info-label">Používateľské meno:</span>
                <span className="user-profile-info-value">{user.username}</span>
              </div>
              <div className="user-profile-info-row">
                <span className="user-profile-info-label">Celé meno:</span>
                <span className="user-profile-info-value">{user.full_name}</span>
              </div>
              {user.internal_number && (
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">Interné číslo pre import:</span>
                  <span className="user-profile-info-value">{user.internal_number}</span>
                </div>
              )}

              <div className="user-profile-info-row">
                <span className="user-profile-info-label">Začiatok spolupráce:</span>
                <span className="user-profile-info-value">{formatDate(user.collaboration_start_date)}</span>
              </div>

              <div className="user-profile-info-row">
                <span className="user-profile-info-label">Email:</span>
                <span className="user-profile-info-value">{user.email}</span>
              </div>
              {user.phone && (
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">Telefón:</span>
                  <span className="user-profile-info-value">{user.phone}</span>
                </div>
              )}
              <div className="user-profile-info-row">
                <span className="user-profile-info-label">Rola:</span>
                <span className="user-profile-info-value">
                  <span className={`user-profile-role-badge user-profile-role-${user.role}`}>
                    {user.role}
                  </span>
                </span>
              </div>
              <div className="user-profile-info-row">
                <span className="user-profile-info-label">Projekt:</span>
                <span className="user-profile-info-value">
                  {(projects.find(p => p.id === user.project_id)?.name) || '—'}
                </span>
              </div>

              {user.accommodation && (
                <div className="user-profile-info-row">
                  <span className="user-profile-info-label">Ubytovanie:</span>
                  <span className="user-profile-info-value">
                    {user.accommodation === 'self' || user.accommodation === 'self_6' ? 'Platí sám' : 
                     user.accommodation === 'company' || user.accommodation === 'company_6' ? 'Platí firma' : 
                     user.accommodation}
                  </span>
                </div>
              )}
              <div className="user-profile-info-row">
                <span className="user-profile-info-label">Vytvorený:</span>
                <span className="user-profile-info-value">
                  {new Date(user.created_at).toLocaleDateString('sk-SK')}
                </span>
              </div>
            </div>
          )}

        </div>

      {canViewBillingRateHistory && (
        <div className="user-profile-section">
          <div className="user-profile-section-header">
            <h2>💶 História fakturačných sadzieb firmy</h2>
          </div>
          {billingRateHistory.length === 0 ? (
            <div className="user-profile-no-terms">
              <p>Základná fakturačná sadzba je 40,00 € pracovný deň a 45,00 € víkend/nočná.</p>
              <p>Pri importe hlavičky typu „Meno / číslo 42,50-47,50“ sa nový riadok pridá iba vtedy, keď sa sadzba zmení.</p>
            </div>
          ) : (
            <div className="user-profile-terms-list">
              {billingRateHistory.map((rate) => (
                <div key={rate.id} className="user-profile-term-item">
                  <div className="user-profile-term-info">
                    <h3>Platné od {new Date(rate.effective_from).toLocaleDateString('sk-SK')}</h3>
                    <p>Pracovný deň / denná / poobedná: {formatMoney(rate.weekday_rate)}</p>
                    <p>Víkend / nočná: {formatMoney(rate.weekend_night_rate)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Documents Section */}
      {/* Private Documents Section */}
      <div className="user-profile-section">
        <div className="user-profile-section-header"><h2>🔒 Súkromné dokumenty</h2></div>
        <PrivateDocuments userId={id} />
      </div>

      <div className="user-profile-section">
        <div className="user-profile-section-header"><h2>📄 Dokumenty</h2></div>
        <DocumentsUpload userId={id} />
      </div>

        {/* Accepted Terms Section */}
        <div className="user-profile-section">
          <div className="user-profile-section-header">
            <h2>📄 Akceptované podmienky</h2>
          </div>

          {acceptedTerms.length === 0 ? (
            <div className="user-profile-no-terms">
              <p>📋 Tento používateľ ešte neakceptoval žiadne podmienky.</p>
            </div>
          ) : (
            <div className="user-profile-terms-list">
              {acceptedTerms.map((term) => (
                <div key={term.id} className="user-profile-term-item">
                  <div className="user-profile-term-info">
                    <h3>{term.title || 'Terms & Conditions'}</h3>
                    <p className="user-profile-term-version">Verzia: {term.version}</p>
                    <p className="user-profile-term-date">
                      Akceptované: {new Date(term.accepted_at).toLocaleDateString('sk-SK', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    {term.ip_address && (
                      <p className="user-profile-term-ip">IP adresa: {term.ip_address}</p>
                    )}
                  </div>
                  <button 
                    onClick={() => downloadPDF(term.id)}
                    className="user-profile-download-button"
                  >
                    📥 Stiahnuť certifikát
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Change Password Section */}
        <div className="user-profile-section">
          <div className="user-profile-section-header">
            <h2>🔐 Zmena hesla</h2>
            {!changePassword && (
              <button 
                onClick={() => setChangePassword(true)} 
                className="user-profile-edit-button"
              >
                🔑 Zmeniť heslo
              </button>
            )}
          </div>

          {changePassword && (
            <form onSubmit={handlePasswordSubmit} className="user-profile-edit-form">
              <div className="user-profile-form-group">
                <label>Aktuálne heslo:</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="user-profile-form-group">
                <label>Nové heslo:</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="user-profile-form-group">
                <label>Potvrdiť nové heslo:</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="user-profile-form-actions">
                <button type="submit" className="user-profile-save-button">
                  💾 Zmeniť heslo
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setChangePassword(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="user-profile-cancel-button"
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
